/**
 * A tradução dos eventos do canal em eventos do `Destino` e em marcas de
 * medição — [spec-008 T030]. Irmão de `transporte-tempo-real.js`, que é quem
 * fala WebRTC e HTTP; aqui não há `RTCPeerConnection`, nem `apiVoz`, nem DOM:
 * entra uma mensagem do data channel, saem chamadas a `emitir(...)` e às
 * marcas de `instrumentacao-de-medicao.js`.
 *
 * A separação é o que torna as TRÊS DERIVAÇÕES testáveis sem montar sessão —
 * e elas são o que este arquivo tem de mais delicado, porque o protocolo
 * medido **não emite** os eventos que o `contracts/transporte.md` pede:
 *
 * | evento do contrato | não existe no fio; derivado de |
 * |---|---|
 * | `transcricaoFinal` ("o turno do usuário fechou") | a REAÇÃO do juiz do provedor: a primeira fala da IA depois da fala dele. **Não** de um silêncio medido por nós — isso seria um segundo juiz de turno, que é o invariante N4 |
 * | `falaDaIaTerminou` | janela de silêncio sobre a fala **da própria IA** (`MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA`, valor medido) — nunca sobre o turno do consultor, por isso não colide com N4 |
 * | `primeiro_audio` (marca) | o primeiro `output_transcript.delta`, cujos `start_ms`/`end_ms` são offsets da linha do tempo do ÁUDIO — o sinal mais próximo do alto-falante que o data channel oferece |
 *
 * ## As marcas são as MESMAS do caminho por turnos (D-6)
 *
 * `fim_da_fala`, `primeiro_audio`, `resposta_completa` e a falha nomeada saem
 * do MESMO módulo impuro (`instrumentacao-de-medicao.js`) e do mesmo núcleo
 * (`medicao-de-voz.js`) que o `porTurnos` usa. Medir cada caminho com um
 * instrumento diferente produz a comparação que já vem decidida — é a razão de
 * existir da D-6.
 *
 * ⚠️ A ÚNICA marca que não é compartilhada byte a byte é `primeiro_audio`:
 * `marcarInicioDeFala` (a do v1) é POSICIONAL e assume o reconhecimento fixo
 * do `voz_ack` como 1ª fala, que aqui não existe. O porquê completo está em
 * `instrumentacao-de-medicao.js::marcarPrimeiroAudioDeConteudo`.
 */
import { criarExecutorDeDelegacao } from './transporte-tempo-real-delegacao'
import { criarControleDaFala } from './transporte-tempo-real-fala'
import {
  marcarFimDaFala,
  marcarPrimeiroAudioDeConteudo,
  marcarFimDeFalaAtual,
  marcarFalhaDaMedicao,
} from './instrumentacao-de-medicao'
import {
  EVENTO_CONSUMO,
  EVENTO_DA_RESPONSES,
  EVENTO_DELEGACAO_CRIADA,
  EVENTO_ERRO,
  EVENTO_SESSAO_FECHADA,
  EVENTO_SESSAO_INICIADA,
  EVENTO_TRANSCRICAO_DA_IA,
  EVENTO_TRANSCRICAO_DO_USUARIO,
  MOTIVO_CANAL_CAIU,
  MOTIVO_ERRO_DO_PROVEDOR,
  MOTIVO_FECHAMENTO_PEDIDO,
  MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA,
} from './transporte-tempo-real-protocolo'

/**
 * @param {Object} params
 * @param {(evento: string, payload?: Object) => void} params.emitir entrega ao
 *   `Destino` registrado. Injetado porque o dono do destino é o transporte.
 * @param {() => boolean} params.pediuFechamento `true` quando fomos NÓS que
 *   pedimos o fim — é o que distingue "encerrou" de "caiu".
 * @param {(mensagem: Object) => boolean} [params.enviar] [T032] põe uma
 *   mensagem no data channel — é por ela que o laço da delegação fecha.
 * @param {() => Object} [params.contexto] [T032] `{idSessao, superficie,
 *   delegar}`; getter, porque o `id_sessao` só nasce na negociação.
 * @param {(segundos: number) => void} [params.aoConsumo] [T035] cada tique do
 *   ledger NATIVO do provedor — é por ele que o teto de duração é vigiado.
 */
// eslint-disable-next-line max-statements
export const criarLeitorDeEventosDaSessao = ({
  emitir,
  pediuFechamento,
  enviar,
  contexto,
  aoConsumo,
}) => {
  let _textoDoUsuario = ''
  // [T032] O último turno FECHADO. `transcricaoFinal` esvazia o buffer, e a
  // demanda da delegação pode chegar depois disso — a ordem entre o
  // `function_call` e a primeira fala da IA não é garantida por nada no
  // protocolo. Sem esta memória, N21 recusaria delegação legítima por "sem
  // turno" só porque a IA falou primeiro.
  let _ultimoTurnoFechado = ''
  // ★ [18/09/2026] O DIÁLOGO do turno — as falas dos DOIS lados, na ordem.
  //
  // Owner, depois de conversar de verdade com a IA: *"falei um monte de coisa
  // (pq estava de fato conversando com a ia e isso é bom) mas só ficou com a
  // última frase"*. E, no mesmo turno, o agente respondeu *"não há um pedido
  // anterior nesta conversa que eu possa retomar"* — dizendo, com todas as
  // letras, que o resto não chegou.
  //
  // A causa estava logo abaixo: cada vez que a IA começava a falar, o texto do
  // consultor virava `_ultimoTurnoFechado` e o buffer ZERAVA. Numa conversa
  // natural — ele fala, ela responde "certo", ele continua — cada interjeição
  // dela APAGAVA o que ele tinha dito antes. Sobrevivia uma fala só.
  //
  // Agora nada é descartado: as falas entram aqui na ordem, com quem disse, e
  // é o diálogo inteiro que vai à delegação.
  let _dialogo = []
  let _textoDaIa = ''
  let _iaFalandoAgora = false
  let _segundosDoProvedor = null
  let _temporizadorDoFimDaFala = null

  /**
   * Tudo que o CONSULTOR disse neste turno, na ordem — nunca a fala da IA.
   *
   * ⚠️ Só a dele: esta função alimenta a GUARDA
   * (`reconciliarIdentificador`, [T040b]), que confere se o identificador que
   * o provedor citou saiu da BOCA do consultor. Misturar a fala da IA aqui
   * faria a guarda aceitar um número que ELA inventou — exatamente o que a
   * reconciliação existe para impedir.
   */
  const _falaDoConsultor = () => {
    const anteriores = _dialogo
      .filter((f) => f.quem === 'consultor')
      .map((f) => f.texto)
    const tudo = anteriores.concat(_textoDoUsuario || []).join(' ').trim()
    return tudo || _ultimoTurnoFechado
  }

  /**
   * O DIÁLOGO do turno, com quem disse cada coisa — é ele que vai ao chat.
   *
   * Owner, 18/09/2026: *"quero que delegue cada um, mostrando o diálogo que
   * aconteceu (agente e usuário)"*. Sem as perguntas dela, as respostas dele
   * ficam soltas: "Doze meses, por favor" não diz de quê.
   *
   * Turno simples (uma fala só, sem conversa) sai SEM rótulo — é o formato de
   * hoje, e mudá-lo poria "Consultor:" na bolha de toda pergunta direta.
   */
  const _dialogoDoTurno = () => {
    const falas = _dialogo.concat(
      _textoDoUsuario ? [{ quem: 'consultor', texto: _textoDoUsuario.trim() }] : []
    )
    if (falas.length <= 1) return _falaDoConsultor()
    return falas
      .map((f) => `${f.quem === 'ia' ? 'IA' : 'Consultor'}: ${f.texto}`)
      .join('\n')
  }

  /**
   * O turno foi DELEGADO: nada dele pode voltar na próxima delegação.
   *
   * ⚠️ A fala ABERTA também sai. Ela não é fechada por ninguém quando a IA
   * não chega a falar — e aí, na delegação seguinte, ficava GRUDADA no começo
   * da pergunta nova ("Doze mesesE do cliente..."). Duplicação que já existia
   * antes do diálogo e que só apareceu quando um teste olhou dois turnos
   * seguidos.
   *
   * `_ultimoTurnoFechado` FICA: é o resgate de quando a demanda chega depois
   * de a IA falar, e apagá-lo faria a delegação legítima ser recusada por
   * "sem turno" (N21).
   */
  const _limparDialogo = () => {
    _dialogo = []
    _textoDaIa = ''
    _textoDoUsuario = ''
  }

  const _delegacao = criarExecutorDeDelegacao({
    emitir,
    enviar,
    textoDoTurno: _falaDoConsultor,
    dialogoDoTurno: _dialogoDoTurno,
    aoConsumirTurno: _limparDialogo,
    contexto,
  })

  // [T036] Os dois primeiros dos três estados da D-11 (texto gerado e áudio
  // reproduzido). O terceiro é o `_delegacao` acima, e este leitor NUNCA o
  // aborta a pedido de `interromper` — é o invariante N17.
  const _fala = criarControleDaFala()

  const _limparTemporizador = () => {
    if (!_temporizadorDoFimDaFala) return
    clearTimeout(_temporizadorDoFimDaFala)
    _temporizadorDoFimDaFala = null
  }

  const encerrarFalaDaIa = () => {
    _limparTemporizador()
    if (!_iaFalandoAgora) return
    _iaFalandoAgora = false
    emitir('falaDaIaTerminou', {})
  }

  /**
   * ★ [T036] `interromper` — os DOIS primeiros estados, e só eles.
   *
   * Para a fala (áudio silenciado + texto descartado) e **não encosta** no
   * `_delegacao`: o job segue, porque *"como está indo?"* não pode matar a
   * consulta que o consultor está perguntando sobre (N17). Quem mata é
   * `cancelarTrabalho()`, que é outro verbo e outra intenção.
   *
   * Com a IA calada é NO-OP: suprimir aqui engoliria a próxima fala, que
   * ninguém mandou parar.
   */
  const interromperFala = () => {
    if (!_iaFalandoAgora) return
    _fala.interromperFalaCorrente()
    encerrarFalaDaIa()
  }

  /**
   * ★ [T036] O cancelamento EXPLÍCITO — o único que mata o job (D-11).
   *
   * ⚠️ A D-11 também prevê a "demanda substituta" como matadora, e ela é
   * INALCANÇÁVEL neste modo: pelo N20 (medido) o protocolo é serial por
   * sessão, e uma segunda demanda com `function_call` pendente é recusada
   * antes de virar delegação. Fica registrado em vez de fingido.
   */
  const cancelarTrabalho = () => {
    _delegacao.abortar()
  }

  const _rearmarFimDaFala = () => {
    _limparTemporizador()
    _temporizadorDoFimDaFala = setTimeout(
      encerrarFalaDaIa,
      MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA
    )
  }

  /**
   * O consultor falou. `marcarFimDaFala` é chamada a CADA delta de propósito:
   * a política do núcleo é "última chamada vence" (`medicao-de-voz.js`), então
   * o valor que sobra é o instante do ÚLTIMO pedaço de fala dele — o mais
   * perto que este canal chega de "ele parou de falar". Ela também rearma o
   * turno da medição, o que é correto inclusive quando ele fala POR CIMA da
   * IA: aí um turno novo começou mesmo.
   */
  const _aoTranscreverUsuario = (evento) => {
    const pedaco = String((evento && evento.delta) || '')
    if (!pedaco) return
    // O consultor voltou a falar: o que a IA disse até aqui fecha e entra no
    // diálogo. É o par simétrico do fechamento da fala DELE, logo abaixo.
    _fecharFalaDaIa()
    _textoDoUsuario += pedaco
    marcarFimDaFala()
    emitir('transcricaoParcial', { texto: _textoDoUsuario })
  }

  /**
   * A IA começou/continuou a falar. A PRIMEIRA fala depois do turno do
   * consultor é a reação do juiz do provedor — é ela que fecha
   * `transcricaoFinal` (derivação 1).
   */
  /** Fecha a fala corrente de um lado e a põe no diálogo. Nada se perde. */
  const _guardarNoDialogo = (quem, texto) => {
    const limpo = String(texto || '').trim()
    if (limpo) _dialogo.push({ quem, texto: limpo })
  }

  const _fecharFalaDaIa = () => {
    if (!_textoDaIa) return
    _guardarNoDialogo('ia', _textoDaIa)
    _textoDaIa = ''
  }

  /** O primeiro pedaço da fala dela: fecha o turno do consultor e avisa. */
  const _aoPrimeiroDeltaDaIa = () => {
    _iaFalandoAgora = true
    if (_textoDoUsuario) {
      emitir('transcricaoFinal', { texto: _textoDoUsuario })
      _ultimoTurnoFechado = _textoDoUsuario
      // ★ GUARDA em vez de descartar — era aqui que a fala sumia.
      _guardarNoDialogo('consultor', _textoDoUsuario)
      _textoDoUsuario = ''
    }
    emitir('falaDaIaComecou', {})
    marcarPrimeiroAudioDeConteudo()
  }

  const _aoTranscreverIa = (evento) => {
    // [T036] "texto gerado → DESCARTA". O provedor não para de gerar quando o
    // consultor manda parar (não existe evento de cancelar fala no fio), então
    // a fala interrompida continua chegando — e daqui para cima ela não sobe.
    // Nem `transcricaoFinal`, nem `falaDaIaComecou`, nem marca de medição:
    // medir `primeiro_audio` de um áudio que ninguém ouviu poria linha falsa
    // no artefato da US-7.
    if (_fala.suprimindo()) {
      _fala.notarDeltaSuprimido()
      return
    }
    if (!_iaFalandoAgora) _aoPrimeiroDeltaDaIa()
    // A fala dela também entra no diálogo: sem as perguntas que ela fez, as
    // respostas dele ficam soltas ("Doze meses, por favor" sem o "de quê?").
    _textoDaIa += String((evento && evento.delta) || '')
    // "Última chamada vence": `resposta_completa` termina no instante do
    // último pedaço falado, sem este módulo saber qual é o último.
    marcarFimDeFalaAtual()
    _rearmarFimDaFala()
  }

  /** Só colhe o número — sem acordar o vigia do teto. */
  const _colherConsumo = (evento) => {
    const uso = (evento && evento.usage) || {}
    if (typeof uso.seconds !== 'number' || !Number.isFinite(uso.seconds)) return
    _segundosDoProvedor = uso.seconds
  }

  /**
   * O tique PERIÓDICO do ledger (cadência medida de 15,0 s). [T035] É o ÚNICO
   * relógio do teto de duração — nunca um `setInterval` de parede.
   *
   * ⚠️ **O total repetido no `session.closed` NÃO passa por aqui**, e isso não
   * é economia: com a sessão já encerrada, o corte por teto não tem o que
   * cortar — e, pior, ele pede `fechar()`, que liga `_fechamentoPedido` e faz
   * o `session.closed` seguinte ser lido como "encerramos nós". Medido: o
   * `session.closed` com `reason: 'expired'` e `usage.seconds` no teto deixou
   * de virar `queda` — a queda anunciada pelo provedor era engolida pelo nosso
   * próprio corte. Por isso `_aoFecharSessao` chama `_colherConsumo`.
   */
  const _aoAtualizarConsumo = (evento) => {
    _colherConsumo(evento)
    if (typeof _segundosDoProvedor !== 'number') return
    if (typeof aoConsumo === 'function') aoConsumo(_segundosDoProvedor)
  }

  const _aoFecharSessao = (evento) => {
    // O `session.closed` repete o total de consumo — vale COLHER mesmo quando
    // o fim foi pedido por nós; é o número que vai ao encerramento. Mas não
    // acorda o vigia do teto: ver o porquê em `_aoAtualizarConsumo`.
    _colherConsumo(evento)
    if (pediuFechamento()) return
    const motivo = (evento && evento.reason) || MOTIVO_CANAL_CAIU
    if (motivo === MOTIVO_FECHAMENTO_PEDIDO) return
    marcarFalhaDaMedicao(MOTIVO_CANAL_CAIU)
    emitir('queda', { motivo })
  }

  /**
   * Erro do provedor. `message` é texto de protocolo (`Unknown client
   * delegation.`, `Missing required parameter: ...`) — nunca valor de
   * credencial: N1 é estrutural neste desenho, o navegador jamais recebeu
   * chave nenhuma (só o `sdp_answer`).
   */
  const _aoReceberErro = (evento) => {
    const erro = (evento && evento.error) || {}
    marcarFalhaDaMedicao(MOTIVO_ERRO_DO_PROVEDOR)
    emitir('erro', {
      motivo: MOTIVO_ERRO_DO_PROVEDOR,
      detalhe: String(erro.message || ''),
    })
  }

  const _MANIPULADORES = {
    [EVENTO_TRANSCRICAO_DO_USUARIO]: _aoTranscreverUsuario,
    [EVENTO_TRANSCRICAO_DA_IA]: _aoTranscreverIa,
    [EVENTO_CONSUMO]: _aoAtualizarConsumo,
    [EVENTO_SESSAO_FECHADA]: _aoFecharSessao,
    [EVENTO_ERRO]: _aoReceberErro,
    // [T032] Os três que até ontem caíam no ramo do silêncio.
    [EVENTO_SESSAO_INICIADA]: _delegacao.aoIniciarSessao,
    [EVENTO_DELEGACAO_CRIADA]: _delegacao.aoCriarDelegacao,
    [EVENTO_DA_RESPONSES]: _delegacao.aoReceberEventoDaResponses,
  }

  /**
   * Evento sem manipulador é ignorado EM SILÊNCIO — é o que permite o provedor
   * emitir algo antes de nós consumirmos. Mesma convenção do parser SSE do
   * módulo, pelo mesmo motivo: evento novo do provedor não é defeito. JSON
   * inválido idem — o canal segue vivo.
   *
   * [T032] Os três que a T030 deixou aqui (`session.started`,
   * `session.delegation.created`, `response.event`) SAÍRAM do silêncio. O que
   * sobra sem manipulador é o que o provedor emite e nós de fato não usamos.
   */
  const aoReceberMensagem = (mensagem) => {
    let evento = null
    try {
      evento = JSON.parse(String((mensagem && mensagem.data) || ''))
    } catch (erro) {
      return
    }
    const manipulador = _MANIPULADORES[evento && evento.type]
    if (manipulador) manipulador(evento)
  }

  /** Zera o estado de sessão — chamado na abertura, nunca no meio dela. */
  const reiniciar = () => {
    _limparTemporizador()
    _delegacao.abortar()
    _fala.limpar()
    _textoDoUsuario = ''
    _ultimoTurnoFechado = ''
    _dialogo = []
    _textoDaIa = ''
    _iaFalandoAgora = false
    _segundosDoProvedor = null
  }

  /**
   * Solta o temporizador e ABORTA a delegação em voo — o consumo sobrevive até
   * o encerramento ser registrado.
   *
   * [T032] Abortar aqui é o que faz a segunda guarda da D-11 valer: depois do
   * teardown, o resultado que a rede ainda entregar não tem correlação
   * corrente e é descartado em vez de falado.
   */
  const limpar = () => {
    _limparTemporizador()
    _delegacao.abortar()
    _fala.limpar()
  }

  const segundosDoProvedor = () => _segundosDoProvedor

  return {
    aoReceberMensagem,
    encerrarFalaDaIa,
    reiniciar,
    limpar,
    segundosDoProvedor,
    // [T036] Os três estados: os dois primeiros aqui, o terceiro intocado.
    interromperFala,
    cancelarTrabalho,
    registrarTrilhaRemota: _fala.registrarTrilhaRemota,
    // ★ [T068] A escuta de barge-in precisa saber QUANDO armar: só se avalia
    // o microfone enquanto a IA fala. Fora disso o limiar mordia a fala normal
    // do consultor (que é o turno dele, não interrupção) e a histerese
    // atravessava turnos. Diferente de `falaSuprimida`, este é um sinal de
    // COMANDO — e é o único; o estado compartilhado `estadoDaFala` continua
    // intocado aqui, senão o barge-in do v1 subiria junto (ver o cabeçalho de
    // `transporte-tempo-real.js`).
    iaFalandoAgora: () => _iaFalandoAgora,
    // [T032] Só diagnóstico/medição — nunca para decidir comportamento.
    delegacaoEmVoo: _delegacao.emVoo,
    falaSuprimida: _fala.suprimindo,
  }
}
